import * as formik from 'formik';
import moment from 'moment';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import { locationType } from '../../../../../enums';
import { ITerminalListItem } from '../../../../../models';
import { mapLocationsToOptions } from '../../../../../utils/mappers';
import DeviceTerminalImagesEdit, { ITerminalImagesPreviewModel } from './terminalImages/DeviceTerminalImagesEdit';

interface ITerminalEditModalProps {
    terminalId: number;
    organizationInformationId: number;
    refetchTerminals(): Promise<void>;
}

const validationSchema = Yup.object<ITerminalListItem>().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    model: Yup.string().required('Šis laukas yra privalomas'),
    key: Yup.string().required('Šis laukas yra privalomas'),
    identificationButtonText_LT: Yup.string()
        .nullable()
        .when('isPersonIdentificationButtonShown', {
            is: true,
            then: Yup.string().nullable().required('Šis laukas yra privalomas')
        }),
    identificationButtonText_EN: Yup.string()
        .nullable()
        .when('isPersonIdentificationButtonShown', {
            is: true,
            then: Yup.string().nullable().required('Šis laukas yra privalomas')
        }),
    identificationButtonText_RU: Yup.string()
        .nullable()
        .when('isPersonIdentificationButtonShown', {
            is: true,
            then: Yup.string().nullable().required('Šis laukas yra privalomas')
        })
});

const TerminalEditModalWindow: FC<IWindowComponentProps<ITerminalEditModalProps>> = (props) => {
    const {
        componentProps: { terminalId, organizationInformationId, refetchTerminals },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [terminal, setTerminal] = useState<ITerminalListItem | null>(null);
    const [ticketTemplateOptions, setTicketTemplateOptions] = useState<fabric.IDropdownOption[] | null>(null);

    const [terminalImagesPreviewModels, setTerminalImagesPreviewModels] = useState<ITerminalImagesPreviewModel[] | null>(null);
    const [floorTypeLocations, setFloorTypeLocations] = useState<ILocationItem[] | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [terminalResp, ticketTemplateOptions, floorTypeLocationsResp] = await Promise.all([
                api.administration.getTerminal(terminalId),
                api.ticketTemplate.getTicketTemplateNames(organizationInformationId),
                api.administration.getLocations(locationType.Level)
            ]);

            setFloorTypeLocations(floorTypeLocationsResp);

            ticketTemplateOptions &&
                setTicketTemplateOptions(
                    ticketTemplateOptions.map((x) => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : ''
                        };
                    })
                );

            if (terminalResp) {
                setTerminal(terminalResp);
                setTerminalImagesPreviewModels(terminalResp.terminalImagesPreviewModels);
            }
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: ITerminalListItem, formikActions: formik.FormikHelpers<ITerminalListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.administration.updateTerminal(values);
            toast.warning('Terminalo pakeitimai išsaugoti, norint kad pasikeitimai įsigaliotų terminale, prašome jį perkrauti');

            formikActions.resetForm();
            await refetchTerminals();
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
            onClose();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {terminal && ticketTemplateOptions && (
                        <formik.Formik validationSchema={validationSchema} initialValues={{ ...terminal }} onSubmit={handleSubmit}>
                            {(terminal) => (
                                <formik.Form>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="name"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Pavadinimas"
                                                        placeholder="Įrašykite terminalo pavadinimą"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.name) || (form.errors.name && form.errors.name)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="model"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Modelis"
                                                        placeholder="Įrašykite terminalo modelį"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.model) || (form.errors.model && form.errors.model)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="key"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Unikalus raktas"
                                                        placeholder="Įrašykite terminalo unikalų raktą"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.key) || (form.errors.key && form.errors.key)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="locationId"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        options={mapLocationsToOptions(floorTypeLocations, true)}
                                                        defaultSelectedKey={field.value}
                                                        {...field}
                                                        onChange={({}, option?: fabric.IDropdownOption) =>
                                                            option && form.setFieldValue(field.name, option.key)
                                                        }
                                                        label="Aukštas"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.locationId) ||
                                                            (form.errors.locationId && form.errors.locationId)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        {!terminal.values.isVirtual && (
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ padding: 0 }}>
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    <formik.Field
                                                        name="startWorkingHours"
                                                        render={({ field, form }: formik.FieldProps<moment.Moment>) => (
                                                            <fabric.TextField
                                                                onChange={({}, value: any) => form.setFieldValue('startWorkingHours', value)}
                                                                defaultValue={moment(terminal.values.startWorkingHours).format('HH:mm')}
                                                                label="Terminalo darbo laiko pradžia"
                                                                type="time"
                                                                errorMessage={
                                                                    (terminal.status && terminal.status.startWorkingHours) ||
                                                                    (form.errors.startWorkingHours && form.errors.startWorkingHours)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    <formik.Field
                                                        name="endWorkingHours"
                                                        render={({ field, form }: formik.FieldProps<moment.Moment>) => (
                                                            <fabric.TextField
                                                                onChange={({}, value: any) => form.setFieldValue('endWorkingHours', value)}
                                                                defaultValue={moment(terminal.values.endWorkingHours).format('HH:mm')}
                                                                label="Terminalo darbo laiko pabaiga"
                                                                type="time"
                                                                errorMessage={
                                                                    (terminal.status && terminal.status.endWorkingHours) ||
                                                                    (form.errors.endWorkingHours && form.errors.endWorkingHours)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {!terminal.values.isVirtual && (
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <formik.Field
                                                    name="ticketTemplateId"
                                                    render={({ field, form }: formik.FieldProps<number>) => (
                                                        <fabric.Dropdown
                                                            label="Bilieto šablono tipas išankstiniams vizitams"
                                                            selectedKey={field.value}
                                                            defaultSelectedKey={form.values.ticketTemplateId}
                                                            options={ticketTemplateOptions}
                                                            onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                            errorMessage={
                                                                (terminal.status && terminal.status.ticketTemplateId) ||
                                                                (form.errors.ticketTemplateId && form.errors.ticketTemplateId)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <formik.Field
                                                    name="servicesDisplayPositionType"
                                                    render={({ field, form }: formik.FieldProps<enums.ServicesDisplayPositionType>) => (
                                                        <fabric.Dropdown
                                                            selectedKey={field.value}
                                                            errorMessage={
                                                                (terminal.status && terminal.status.servicesDisplayPositionType) ||
                                                                (form.errors.servicesDisplayPositionType && form.errors.servicesDisplayPositionType)
                                                            }
                                                            options={enums.servicesDisplayPositionType.displayTypesArray()}
                                                            onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                            defaultSelectedKey={form.values.servicesDisplayPositionType}
                                                            label="Lango išdėstymas"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="headerText_LT"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Terminalo antraštės tekstas LT"
                                                        value={`${field.value}`}
                                                        placeholder="Įrašykite terminalo antraštės tekstą"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.headerText_LT) ||
                                                            (form.errors.headerText_LT && form.errors.headerText_LT)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="headerText_EN"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Terminalo antraštės tekstas EN"
                                                        value={`${field.value}`}
                                                        placeholder="Įrašykite terminalo antraštės tekstą"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.headerText_EN) ||
                                                            (form.errors.headerText_EN && form.errors.headerText_EN)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="headerText_RU"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Terminalo antraštės tekstas RU"
                                                        value={`${field.value}`}
                                                        placeholder="Įrašykite terminalo antraštės tekstą"
                                                        errorMessage={
                                                            (terminal.status && terminal.status.headerText_RU) ||
                                                            (form.errors.headerText_RU && form.errors.headerText_RU)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    {!terminal.values.isVirtual && (
                                        <>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isPersonIdentificationButtonShown"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Ar rodyti „Įvesti asmens kodą“ mygtuką?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                                onChange={() => {
                                                                    form.setFieldValue(field.name, !field.value);
                                                                    if (!field.value) {
                                                                        if (terminal.values.identificationButtonText_LT === null) {
                                                                            form.setFieldValue('identificationButtonText_LT', 'Įvesti asmens kodą');
                                                                        }
                                                                        if (terminal.values.identificationButtonText_EN === null) {
                                                                            form.setFieldValue('identificationButtonText_EN', 'Enter person code');
                                                                        }
                                                                        if (terminal.values.identificationButtonText_RU === null) {
                                                                            form.setFieldValue('identificationButtonText_RU', 'Введите личный код');
                                                                        }
                                                                        form.validateForm();
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            {terminal.values.isPersonIdentificationButtonShown && (
                                                <div className="ms-Grid-row">
                                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                        <formik.Field
                                                            name="identificationButtonLocation"
                                                            render={({ field, form }: formik.FieldProps<enums.IdentificationButtonLocationEnum>) => (
                                                                <fabric.Dropdown
                                                                    selectedKey={field.value}
                                                                    options={enums.identificationButtonLocationEnum.displayTypesArray()}
                                                                    onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                                    defaultSelectedKey={form.values.identificationButtonLocation}
                                                                    label="Atvaizdavimo vieta"
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                        <formik.Field
                                                            name="identificationButtonText_LT"
                                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                                <fabric.TextField
                                                                    {...field}
                                                                    label="Pavadinimas LT"
                                                                    placeholder="Įvesti asmens kodą"
                                                                    errorMessage={
                                                                        (terminal.status && terminal.status.identificationButtonText_LT) ||
                                                                        form.errors.identificationButtonText_LT
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                        <formik.Field
                                                            name="identificationButtonText_EN"
                                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                                <fabric.TextField
                                                                    {...field}
                                                                    label="Pavadinimas EN"
                                                                    placeholder="Enter personal code"
                                                                    errorMessage={
                                                                        (terminal.status && terminal.status.identificationButtonText_EN) ||
                                                                        form.errors.identificationButtonText_EN
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                        <formik.Field
                                                            name="identificationButtonText_RU"
                                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                                <fabric.TextField
                                                                    {...field}
                                                                    label="Pavadinimas RU"
                                                                    placeholder="Введите личный код"
                                                                    errorMessage={
                                                                        (terminal.status && terminal.status.identificationButtonText_RU) ||
                                                                        form.errors.identificationButtonText_RU
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isArrivalConfirmationButtonShown"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Ar rodyti „Atvykimo patvirtinimo“ mygtuką?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isRepresentedUsersShown"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Ar rodyti Atstovaujamo asmens langą?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isPrinterStatusReportable"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Pranešti apie printerio būseną?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isServicesDescriptionButtonsShown"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Rodyti paslaugų aprašymo mygtukus?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isVisitSelectionEnabled"
                                                        render={({ field, form }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Įjungti vizitų pasirinkimo funkciją?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isSupportingFriendlyOrganizations"
                                                        render={({ field }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Ar komunikuoti su draugiškomis įstaigomis?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                    <formik.Field
                                                        name="isPersonalCodeShowingInInputField"
                                                        render={({ field }: formik.FieldProps<boolean>) => (
                                                            <fabric.Checkbox
                                                                label="Ar rodyti įvedamą asmens kodą?"
                                                                {...field}
                                                                value={`${field.value}`}
                                                                style={{ paddingTop: '20px' }}
                                                                checked={field.value}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {terminal.values.isVirtual && (
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6" style={{ marginTop: '35px' }}>
                                                <formik.Field
                                                    name="isVisitButtonShown"
                                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                                        <fabric.Checkbox
                                                            label="Ar rodyti vizitų mygtuką?"
                                                            {...field}
                                                            value={`${field.value}`}
                                                            style={{ paddingTop: '20px' }}
                                                            checked={field.value}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {terminal.values.servicesDisplayPositionType !== enums.servicesDisplayPositionType.Middle &&
                                        terminalImagesPreviewModels && (
                                            <div>
                                                <fabric.Separator />
                                                <fabric.Label>Terminale atvaizduojami paveikslėliai:</fabric.Label>
                                                <fabric.Pivot>
                                                    <fabric.PivotItem headerText="LT">
                                                        <DeviceTerminalImagesEdit
                                                            terminalId={terminal.values.id}
                                                            terminalImagesPreviewModel={
                                                                terminalImagesPreviewModels?.filter((x) => x.locale == 'lt-LT')[0]
                                                            }
                                                            fetchData={fetchData}
                                                        />
                                                    </fabric.PivotItem>
                                                    <fabric.PivotItem headerText="EN">
                                                        <DeviceTerminalImagesEdit
                                                            terminalId={terminal.values.id}
                                                            terminalImagesPreviewModel={
                                                                terminalImagesPreviewModels?.filter((x) => x.locale == 'en-US')[0]
                                                            }
                                                            fetchData={fetchData}
                                                        />
                                                    </fabric.PivotItem>
                                                    <fabric.PivotItem headerText="RU">
                                                        <DeviceTerminalImagesEdit
                                                            terminalId={terminal.values.id}
                                                            terminalImagesPreviewModel={
                                                                terminalImagesPreviewModels?.filter((x) => x.locale == 'ru-RU')[0]
                                                            }
                                                            fetchData={fetchData}
                                                        />
                                                    </fabric.PivotItem>
                                                </fabric.Pivot>
                                            </div>
                                        )}
                                    <div className="ms-Grid-row">
                                        <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                            <fabric.PrimaryButton
                                                className="ms-slideRightIn40 ms-textAlignRight"
                                                text="Išsaugoti"
                                                disabled={terminal.isSubmitting || !terminal.isValid || !terminal.dirty}
                                                type="submit"
                                            />
                                        </div>
                                    </div>
                                </formik.Form>
                            )}
                        </formik.Formik>
                    )}
                </>
            )}
        </>
    );
};

export default TerminalEditModalWindow;
